import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button, Stack } from '@mui/material';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import {
  useBranchesDictionary,
  useServicesDictionary,
  useStrategiesDictionary,
  useUnsubscribeClientsStatistic,
} from './queries';
import { FilterSection } from '../../../components/FilterSection';
import { ControlledSelectField } from '../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextField } from '../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../components/FormControlsV2/FormProvider';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { getCorrectServiceType, sortColumnFn } from '../../../components/TableGridLocal/filters';
import { currencyInfo$ } from '../../../effector/currencies';
import { useDebouncedState } from '../../../hooks/useDebouncedState';
import { Criteria, Filter, PageParams } from '../../../types/pagination';
import { UnsubscribeClient } from '../../../types/unsubscribeClient';
import { formatMoney, getPercentage } from '../../../utils';
import { PAGE_STATISTIC_CLIENT_DETAILS_INFO } from '../../Routes';

type FormFields = {
  strategy: string[];
  service: string[];
  agreement: string;
  clientId: string;
};

export const UnsubscribeClients: React.FC = () => {
  const [formFilters, setFormFilters] = useDebouncedState<Filter[]>([], 500);
  const isFirstRender = useRef(true);

  const currencyStore = useUnit(currencyInfo$);

  const { data: branchesResult } = useBranchesDictionary();
  const { data: strategiesResult } = useStrategiesDictionary();
  const { data: servicesResult } = useServicesDictionary();
  const { data, isPending: isLoading, mutate: loadStatistic } = useUnsubscribeClientsStatistic();

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      strategy: [],
      service: [],
      agreement: '',
      clientId: '',
    },
  });

  const loadClients = useCallback(
    (params: PageParams) => {
      loadStatistic({
        ...params,
        filters: formFilters.concat(params.filters || []),
      });
    },
    [formFilters, loadStatistic],
  );

  const updatedData = useMemo(() => {
    return (data?.items || []).map((item) => ({
      ...item,
      currency: item.currency === 'SUR' ? 'RUB' : item.currency,
    }));
  }, [data]);

  const strategies = useMemo(() => strategiesResult?.result?.map(({ name }) => name), [strategiesResult]);
  const currencies = useMemo(() => currencyStore.map(({ code }) => (code === 'SUR' ? 'RUB' : code)), [currencyStore]);
  const branches = useMemo(() => branchesResult?.result?.map(({ name }) => name), [branchesResult]);
  const strategiesOptions = useMemo(
    () => strategiesResult?.result?.map((strategy) => ({ value: strategy.name, label: strategy.name })) || [],
    [strategiesResult],
  );
  const servicesOptions = useMemo(
    () => servicesResult?.result?.map((service) => ({ value: service.id, label: service.name })) || [],
    [servicesResult],
  );

  const columns = useMemo<MRT_ColumnDef<UnsubscribeClient>[]>(() => {
    const clientDetailsPath = (agreementId?: string) =>
      `${PAGE_STATISTIC_CLIENT_DETAILS_INFO}/${agreementId}?source=unsubscribe`;

    const items: MRT_ColumnDef<UnsubscribeClient>[] = [
      {
        accessorKey: 'clientId',
        header: 'MasterId',
        filterVariant: 'text',
        Cell: ({ cell }) => (
          <Link to={clientDetailsPath(cell?.row?.original?.agreementId)}>{cell?.row?.original?.clientId}</Link>
        ),
      },
      {
        accessorKey: 'agreement',
        header: 'Г/С',
        filterVariant: 'text',
        Cell: ({ cell }) => (
          <Link target="_blank" to={`${window.location.origin}${clientDetailsPath(cell?.row?.original?.agreementId)}`}>
            {cell?.row?.original?.agreement}
          </Link>
        ),
      },
      {
        header: 'Портфель',
        accessorKey: 'portfolio',
        accessorFn: (originalRow) => {
          const value = originalRow.currency === 'RUB' ? originalRow.portfolioSUR : originalRow.portfolio;
          return value;
        },
        sortingFn: (col, col2) => sortColumnFn(col.original, col2.original, 'portfolioSUR'),
        Cell: ({ cell }) => {
          const value =
            cell.row.original.currency === 'RUB' ? cell.row.original.portfolioSUR : cell.row.original.portfolio;

          if (!value) {
            return '';
          }

          return formatMoney(value, cell.row.original.currency);
        },
      },
      {
        header: 'В инструментах',
        accessorKey: 'positions',
        accessorFn: (originalRow) => {
          const value = originalRow.currency === 'RUB' ? originalRow.positionsSUR : originalRow.positions;
          return value;
        },
        sortingFn: (col, col2) => sortColumnFn(col.original, col2.original, 'positionsSUR'),
        Cell: ({ cell }) => {
          const value =
            cell.row.original.currency === 'RUB' ? cell.row.original.positionsSUR : cell.row.original.positions;

          if (!value) {
            return '';
          }

          return formatMoney(value, cell.row.original.currency);
        },
      },
      {
        header: 'Валюта',
        accessorKey: 'currency',
        filterVariant: 'select',
        filterSelectOptions: currencies,
        size: 50,
      },
      {
        accessorKey: 'free',
        header: 'Свободно',
        accessorFn: (originalRow) => {
          const value = originalRow.currency === 'RUB' ? originalRow.freeSUR : originalRow.free;
          return value;
        },
        sortingFn: (col, col2) => sortColumnFn(col.original, col2.original, 'freeSUR'),
        Cell: ({ cell }) => {
          const value = cell.row.original.currency === 'RUB' ? cell.row.original.freeSUR : cell.row.original.free;

          if (!value) {
            return '';
          }

          return formatMoney(value, cell.row.original.currency);
        },
      },
      {
        accessorKey: 'minDiff',
        header: 'Мин. откл.',
        Cell: ({ cell }) => {
          if (cell.row.original.minDiff == null) {
            return '';
          }

          return getPercentage(cell.row.original.minDiff);
        },
      },
      {
        accessorKey: 'withdrawFundsTotal',
        header: 'Частичный вывод',
        Cell: ({ cell }) => {
          const value = cell.row.original.withdrawFundsTotal;

          if (!value) {
            return '';
          }

          return formatMoney(value, cell.row.original.currency);
        },
      },
      {
        accessorKey: 'bringFundsTotal',
        header: 'Дозавод',
        Cell: ({ cell }) => {
          const value = cell.row.original.bringFundsTotal;

          if (!value) {
            return '';
          }

          return formatMoney(value, cell.row.original.currency);
        },
      },
      {
        accessorKey: 'deviation',
        header: 'Отклонение',
        size: 100,
        Cell: ({ cell }) => {
          if (cell.row.original.deviation == null) {
            return '';
          }

          return getPercentage(cell.row.original.deviation);
        },
      },
      {
        accessorKey: 'refillPercent',
        header: 'Пополнения (%)',
        filterVariant: 'range',
        size: 100,
        Cell: ({ cell }) => {
          if (!cell.row.original.refillPercent) {
            return '';
          }

          return getPercentage(cell.row.original.refillPercent);
        },
      },
      {
        accessorKey: 'pnlTotal',
        header: 'Финансовый результат',
        filterVariant: 'range',
        Cell: ({ cell }) => {
          if (!cell.row.original.pnlTotal) {
            return '';
          }

          return getPercentage(cell.row.original.pnlTotal);
        },
      },
      {
        accessorKey: 'lifetime',
        header: 'Время на стратегии',
        filterVariant: 'range',
      },
      {
        accessorKey: 'lastStrategyName',
        id: 'strategy',
        header: 'Стратегия',
        filterVariant: 'multi-select',
        filterSelectOptions: strategies,
      },
      {
        accessorKey: 'serviceType',
        id: 'service',
        header: 'Услуга',
        filterVariant: 'multi-select',
        filterSelectOptions: ['Автоследование', 'Автоконсультирование', 'Накопилка'],
        size: 150,
        Cell: ({ cell }) => getCorrectServiceType(cell.row.original.serviceType),
      },
      {
        accessorKey: 'unsubscriptionDate',
        header: 'Отключен',
        Cell: ({ cell }) => {
          const date = cell?.row?.original?.unsubscriptionDate;
          return <span>{date ? moment(date).format('DD.MM.YYYY HH:mm') : ''}</span>;
        },
      },
      {
        accessorKey: 'branchName',
        header: 'Офис',
        filterVariant: 'select',
        filterSelectOptions: branches,
      },
    ];

    return items;
  }, [branches, currencies, strategies]);

  const columnSorts = useMemo<MRT_SortingState>(
    () => [
      {
        desc: true,
        id: 'unsubscriptionDate',
      },
    ],
    [],
  );

  const formValues = watch();

  const memoizedFormValues = useMemo(() => JSON.stringify(formValues), [formValues]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const values: FormFields = JSON.parse(memoizedFormValues || '{}');

    const filters = Object.entries(values).flatMap(([key, value]) => {
      if (typeof value === 'string' && value.trim()) {
        return {
          fieldName: key,
          filterCriteria: Criteria.Contains,
          matches: [value],
        };
      }

      if (typeof value === 'number') {
        return {
          fieldName: key,
          filterCriteria: Criteria.Equals,
          matches: [value],
        };
      }

      if (Array.isArray(value) && value.length) {
        return {
          fieldName: key,
          filterCriteria: Criteria.Equals,
          matches: value.map((item) => item),
        };
      }

      return [];
    });

    setFormFilters(filters);
  }, [memoizedFormValues, setFormFilters]);

  return (
    <>
      <FilterSection>
        <form>
          <FormProvider control={control} errors={errors}>
            <Stack direction="row" gap={2}>
              <ControlledTextField name="agreement" label="Г/С" />
              <ControlledTextField name="clientId" label="Master ID" />
            </Stack>

            <Stack direction="row" gap={2}>
              {strategiesOptions && (
                <ControlledSelectField name="strategy" label="Стратегии" multiple options={strategiesOptions} />
              )}
              {servicesOptions && <ControlledSelectField name="service" label="Услуга" options={servicesOptions} />}
            </Stack>
          </FormProvider>

          <Button variant="outlined" color="primary" size="large" fullWidth sx={{ mt: 2 }} onClick={() => reset()}>
            Сбросить
          </Button>
        </form>
      </FilterSection>
      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="statistic-unsubscribe-clients"
          serverMode
          pageSize={25}
          columns={columns}
          data={updatedData}
          isLoading={isLoading}
          columnSorts={columnSorts}
          totalRowCount={data?.total || 0}
          onFetchData={loadClients}
        />
      </div>
    </>
  );
};
